/**
 * Learn System View Component
 * Educational system overview and learning modules
 */

import React from 'react';
import { 
  GraduationCap, 
  BookOpen, 
  ClipboardCheck, 
  TrendingUp, 
  Users, 
  Brain, 
  Target, 
  MessageSquare, 
  Network, 
  Play, 
  Pause, 
  Award, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Lightbulb, 
  Activity, 
  Info, 
  FileText, 
  BarChart 
} from 'lucide-react';

const LearnSystemView = () => {
  const learningModules = [
    {
      id: 1,
      title: "Complete Educational Lifecycle",
      icon: <Network className="w-6 h-6" />,
      color: "bg-blue-500",
      content: "The A2A system models the entire educational ecosystem, from teaching to teacher evaluation. This comprehensive approach demonstrates how AI agents collaborate across the learning cycle - teachers, students, assessments, feedback, adaptive goals, self-assessment, and teacher effectiveness.",
      benefits: [
        "Automating assessment and feedback processes",
        "Providing objective teacher KPIs and effectiveness metrics",
        "Enabling personalized learning at scale",
        "Creating continuous improvement loops"
      ]
    },
    {
      id: 2,
      title: "Complete Educational Ecosystem",
      icon: <Network className="w-6 h-6" />,
      color: "bg-blue-500",
      content: "The A2A system models the complete educational cycle, demonstrating how specialized agents work together to create a seamless learning experience from initial teaching through final evaluation."
    },
    {
      id: 3,
      title: "Multi-Level Feedback Loops",
      icon: <TrendingUp className="w-6 h-6" />,
      color: "bg-purple-500",
      content: "The system creates multiple feedback loops where student performance influences adaptive learning goals, teacher effectiveness metrics adjust teaching strategies, and self-assessment data helps develop metacognitive skills, continuously improving the learning ecosystem."
    },
    {
      id: 4,
      title: "Data-Driven Teacher Evaluation",
      icon: <BarChart className="w-6 h-6" />,
      color: "bg-green-500",
      content: "A2A systems provide objective teacher effectiveness metrics based on student outcomes, engagement levels, learning velocity, and improvement rates. Subject panels and headmasters receive comprehensive KPIs for data-driven decision making."
    },
    {
      id: 5,
      title: "Personalized & Adaptive Learning",
      icon: <Target className="w-6 h-6" />,
      color: "bg-orange-500",
      content: "The system automatically generates personalized learning goals for each student based on assessment results, considering individual strengths, weaknesses, learning pace, and preferred learning styles."
    }
  ];

  const lifecycleComponents = [
    {
      title: "Student Journey",
      items: [
        "Receive instruction",
        "Take assessments", 
        "Get personalized feedback",
        "Follow adaptive goals",
        "Self-assess learning"
      ]
    },
    {
      title: "Teacher Journey", 
      items: [
        "Deliver lessons",
        "Track student progress",
        "Receive KPI metrics",
        "Get improvement insights",
        "Professional development"
      ]
    },
    {
      title: "Administrative Journey",
      items: [
        "Monitor teacher effectiveness",
        "Review objective KPIs",
        "Make data-driven decisions",
        "Allocate resources",
        "Strategic planning"
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Main Learning Modules */}
      <div className="space-y-6">
        {learningModules.map((module) => (
          <div key={module.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-start space-x-4">
              <div className={`${module.color} p-3 rounded-lg text-white flex-shrink-0`}>
                {module.icon}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{module.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{module.content}</p>
                
                {module.benefits && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">Why This Matters:</h4>
                    <ul className="space-y-1">
                      {module.benefits.map((benefit, idx) => (
                        <li key={idx} className="text-sm text-blue-800 flex items-start space-x-2">
                          <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Complete Lifecycle Components */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-xl text-white">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-white bg-opacity-20 p-2 rounded-full">
            <Info className="w-6 h-6" />
          </div>
          <h3 className="text-xl font-bold">Complete Lifecycle Components</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {lifecycleComponents.map((component, idx) => (
            <div key={idx} className="bg-white bg-opacity-10 p-4 rounded-lg">
              <h4 className="font-semibold mb-3 text-blue-100">{component.title}</h4>
              <ul className="space-y-2">
                {component.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="text-sm text-blue-50 flex items-start space-x-2">
                    <div className="w-1.5 h-1.5 bg-blue-200 rounded-full mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Feedback Loops */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-green-100 p-2 rounded-lg">
            <TrendingUp className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Feedback Loops in the System</h3>
        </div>
        
        <div className="space-y-4">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-900 mb-2">Student Feedback Loop</h4>
            <p className="text-sm text-green-800">
              Assessment results → Personalized feedback → Adaptive goals → Improved performance → New assessments
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnSystemView;
